import React, { useContext, useEffect, useRef, useState } from 'react';
import chevDownIcon from 'src/assets/images/icons/chevron-down.svg';
import { IJob, IJobsFilters } from 'src/types';
import { Link } from 'react-router-dom';
import { JobStatus } from 'src/enums';
import moment from 'moment';
import Paginator from 'src/components/Paginator';
import axiosInstance from 'src/services/axiosInstance.service';
import { AppContext } from 'src/utils';
import * as notify from 'src/services/notification.service';

interface IProps {
  jobs: IJob[];
  currentPage: number;
  filtersChanged: (searchParams: any) => void;
  sortingChanged: (sortBy: string) => void;
  fetchPage: (pageNumber: number) => void;
  setRecordsPerPage: (recordsCount: number) => void;
  currentRecordsPerPage: number;
  totalRecordsAvailable: number;
  totalPages: number;
  isLoading: boolean;
  sortBy: string;
  refreshJobs: (x?: boolean) => void;
}

function isJobMonitored(job: IJob): boolean {
  return job.monitor === 'Never' || job.monitor === 'undefined' || job.monitor === undefined
    ? false
    : true;
}

const JobsGrid: (props: IProps) => JSX.Element = (props: IProps) => {
  const isInitialMount = useRef(true);
  const { currentUser } = useContext(AppContext);

  const [filters, setFilters] = useState<IJobsFilters>({
    startedDate: '',
    customerType: '',
  });

  // const [frequencyOptionsVisibility, setFrequencyOptionsVisibility] =
  //   useState('frequency-options-hide');

  const icons: { [key: string]: any } = {
    [JobStatus.InProgress]: require('src/assets/images/icons/inprogress.svg').default,
    [JobStatus.Complete]: require('src/assets/images/icons/complete.svg').default,
    [JobStatus.Error]: require('src/assets/images/icons/error.svg').default,
  };

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      props.filtersChanged(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const pauseScreen = async (jobId: string, startedAt: string) => {
    try {
      let disableJobResponse = await axiosInstance.put(
        `customer/${currentUser?.customer_id}/user/${currentUser?.id}/jobs/${jobId}/disable`,
        { startedAt },
      );
      console.log(disableJobResponse);
      if (disableJobResponse.status === 200 && disableJobResponse.data.status === 200) {
        notify.success(disableJobResponse.data.data.message);
        props.refreshJobs(true);
      }
    } catch (err) {
      notify.error('Error disabling job.');
      console.error(err);
    }
  };

  const resumeScreen = async (jobId: string, startedAt: string) => {
    try {
      let enableJobResponse = await axiosInstance.put(
        `customer/${currentUser?.customer_id}/user/${currentUser?.id}/jobs/${jobId}/enable`,
        { startedAt },
      );
      console.log(enableJobResponse);
      if (enableJobResponse.status === 200 && enableJobResponse.data.status === 200) {
        notify.success(enableJobResponse.data.data.message);
        props.refreshJobs(true);
      }
    } catch (err) {
      notify.error('Error enabling job.');
      console.error(err);
    }
  };

  const archiveList = async (jobId: string, startedAt: string) => {
    try {
      let archiveJobResponse = await axiosInstance.put(
        `customer/${currentUser?.customer_id}/user/${currentUser?.id}/jobs/${jobId}/archive`,
        { startedAt },
      );
      console.log(archiveJobResponse);
      if (archiveJobResponse.status === 200 && archiveJobResponse.data.status === 200) {
        props.refreshJobs(true);
        notify.success(archiveJobResponse.data.data.message);
      }
    } catch (err) {
      notify.error('Error archiving job.');
      console.error(err);
    }
  };

  // const toggleFrequencyOptions = () => {
  //   if (frequencyOptionsVisibility === 'frequency-options-hide') {
  //     setFrequencyOptionsVisibility('frequency-options-show');
  //   } else if (frequencyOptionsVisibility === 'frequency-options-show') {
  //     setFrequencyOptionsVisibility('frequency-options-hide');
  //   }
  // };

  return (
    <>
      <div data-testid="jobs-grid">
        <form>
          <div className="d-flex justify-content-between">
            <div className="d-flex gap-3 flex-wrap">
              <div className="select-with-left-icon">
                <select
                  className="form-control"
                  style={{ backgroundImage: `url(${chevDownIcon})` }}
                  value={filters.startedDate}
                  onChange={(e) => {
                    setFilters({ ...filters, startedDate: e.target.value });
                  }}
                >
                  <option value={''}>All Jobs</option>
                  <option value={moment().subtract(1, 'month').format('YYYY-MM-DD')}>
                    1 Month
                  </option>
                  <option value={moment().subtract(3, 'months').format('YYYY-MM-DD')}>
                    3 Months
                  </option>
                  <option value={moment().subtract(12, 'months').format('YYYY-MM-DD')}>
                    12 Months
                  </option>
                  <option value={moment().subtract(3, 'years').format('YYYY-MM-DD')}>
                    3 Years
                  </option>
                </select>
                <img src={require('src/assets/images/icons/allFiles-icon.svg').default} alt="" />
              </div>
              <div className="select-with-left-icon">
                <select
                  name="allCustomers"
                  className="form-control"
                  style={{ backgroundImage: `url(${chevDownIcon})` }}
                  value={filters.customerType}
                  onChange={(e) => {
                    setFilters({ ...filters, customerType: e.target.value });
                  }}
                >
                  <option value={''}>All Customers</option>
                  <option value={'Business'}>Businesses</option>
                  <option value={'Individual'}>Individuals</option>
                </select>
                <img src={require('src/assets/images/icons/customers-icon.svg').default} alt="" />
              </div>
            </div>
          </div>
        </form>

        <div className="batchJobsListing mt-4">
          {props.jobs.map((job) => {
            return (
              <Link
                key={job.id}
                to={`/batch-screening/job-entities/${job.id}`}
                style={{
                  pointerEvents:
                    job.job_status === JobStatus.Complete || job.job_status === JobStatus.Error
                      ? 'auto'
                      : 'none',
                }}
              >
                <li
                  className="d-flex gap-4 flex-nowrap justify-content-between p-4 hvg-bg"
                  data-testid="job-item"
                >
                  <div className="d-flex gap-1 align-items-start">
                    <img className="job-batch-status-icon" src={icons[job.job_status]} alt="" />
                    <div className="job-status-content">
                      <h6>{job.job_title}</h6>
                      <span className={`job-status ${job.monitor?.toLowerCase()}`}>
                        {job.monitor === 'undefined' || job.monitor === 'Never'
                          ? 'No Monitoring'
                          : job.monitor}
                      </span>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between flex-nowrap gap-4  right-fields">
                    <div className="batch-job-col">
                      <span>Customer Type</span>

                      <p className="d-flex align-items-center gap-2">
                        {job.customer_type.toLowerCase() === 'business' ? (
                          <img
                            src={require('src/assets/images/icons/ct-business.svg').default}
                            alt=""
                          />
                        ) : (
                          <img
                            src={require('src/assets/images/icons/ct-individual.svg').default}
                            alt=""
                          />
                        )}
                        <span className="mb-0"> {job.customer_type} </span>
                      </p>
                    </div>
                    <div className="batch-job-col">
                      <span>Total Records</span>
                      <p>
                        {[JobStatus.Error, JobStatus.InProgress, JobStatus.Pending].includes(
                          job.job_status as JobStatus,
                        )
                          ? '-'
                          : job.total_input_records}
                      </p>
                    </div>
                    <div className="batch-job-col">
                      <span>Matched Customers</span>
                      <p>
                        {[JobStatus.Error, JobStatus.InProgress, JobStatus.Pending].includes(
                          job.job_status as JobStatus,
                        )
                          ? '-'
                          : job.matched_customers}
                      </p>
                    </div>
                    <div className="batch-job-col">
                      <span>Potential Matches</span>
                      <p>
                        {[JobStatus.Error, JobStatus.InProgress, JobStatus.Pending].includes(
                          job.job_status as JobStatus,
                        )
                          ? '-'
                          : job.potential_matches}
                      </p>
                    </div>
                    <div className="batch-job-col">
                      <span>No Matches</span>
                      <p>
                        {[JobStatus.Error, JobStatus.InProgress, JobStatus.Pending].includes(
                          job.job_status as JobStatus,
                        )
                          ? '-'
                          : job.no_matches}
                      </p>
                    </div>
                    <div className="batch-job-col">
                      <span>Last Screened</span>
                      <p>
                        {[JobStatus.Error, JobStatus.InProgress, JobStatus.Pending].includes(
                          job.job_status as JobStatus,
                        ) ||
                        !job.updated_at ||
                        !job.completed_time
                          ? '-'
                          : `${moment(job.updated_at).format('DD/MM/YYYY')} ${moment(
                              job.completed_time,
                              'HH:mm:SS',
                            ).format('HH:mm:SS')}`}
                      </p>
                    </div>
                    <div className="batch-job-col my-auto">
                      <div className="batch-more">
                        <img
                          className="batch-more-icon"
                          src={require('src/assets/images/icons/three-dots.svg').default}
                          alt=""
                        />

                        <div className="batch-more-content">
                          {/* <div>
                            <img
                              src={require('src/assets/images/icons/screen-list-icon.svg').default}
                              alt=""
                            />
                            Screen list now
                          </div> */}
                          {/* <div className={frequencyOptionsVisibility + '-parent'}>
                            <img
                              src={require('src/assets/images/icons/edit-screen-icon.svg').default}
                              alt=""
                            />
                            Edit screening frequency
                            <img
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleFrequencyOptions();
                              }}
                              className="chevron-down"
                              src={require('src/assets/images/icons/chevron-down.svg').default}
                              alt=""
                            />
                          </div> */}
                          {/* <div className={'frequency-options ' + frequencyOptionsVisibility}>
                            <form>
                              <div className="form__group">
                                <div className="form__radio-group">
                                  <input
                                    type="radio"
                                    name="frequency"
                                    id="daily"
                                    className="form__radio-input"
                                  />
                                  <label
                                    className="form__label-radio form__radio-label"
                                    htmlFor="daily"
                                  >
                                    <span className="form__radio-button"></span> Daily
                                  </label>
                                </div>

                                <div className="form__radio-group">
                                  <input
                                    type="radio"
                                    name="frequency"
                                    id="weekly"
                                    className="form__radio-input"
                                  />
                                  <label
                                    className="form__label-radio form__radio-label"
                                    htmlFor="weekly"
                                  >
                                    <span className="form__radio-button"></span>Weekly
                                  </label>
                                </div>
                                <div className="form__radio-group">
                                  <input
                                    type="radio"
                                    name="frequency"
                                    id="monthly"
                                    className="form__radio-input"
                                  />
                                  <label
                                    className="form__label-radio form__radio-label"
                                    htmlFor="monthly"
                                  >
                                    <span className="form__radio-button"></span>Monthly
                                  </label>
                                </div>
                              </div>
                            </form>
                          </div> */}

                          {
                            isJobMonitored(job)
                              ? (
                                <div
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    pauseScreen(job.id, job.started_at);
                                  }}
                                >
                                  <img
                                    src={require('src/assets/images/icons/pause-icon.svg').default}
                                    alt=""
                                  />
                                  Stop monitoring
                                </div>
                              ) 
                              : (
                                <div
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();

                                    resumeScreen(job.id, job.started_at);
                                  }}
                                  className="color-green"
                                >
                                  <img
                                    src={require('src/assets/images/icons/bell-green.svg').default}
                                    alt=""
                                  />
                                  Start monitoring
                                </div>
                              )
                          }
                          
                          <div
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (window.confirm('Are you sure you wish to archive this job?')) {
                                console.log('archive called');
                                archiveList(job.id, job.started_at);
                              } else {
                                console.log('nothing');
                              }
                            }}
                            className="color-pink"
                          >
                            <img
                              src={require('src/assets/images/icons/archive-icon.svg').default}
                              alt=""
                            />
                            Archive list
                            <div></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </Link>
            );
          })}
        </div>

        <Paginator
          fetchPage={props.fetchPage}
          setRecordsPerPage={props.setRecordsPerPage}
          currentPage={props.currentPage}
          totalPages={props.totalPages}
          currentRecordsCount={props.jobs.length}
          currentRecordsPerPage={props.currentRecordsPerPage}
          totalRecordsAvailable={props.totalRecordsAvailable}
        />
      </div>
    </>
  );
};

export default JobsGrid;
